/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  Modal,
  Box,
  Link,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import { 
  addAttendanceActivityMark, 
  addRemoteAttendance, 
  getLastAttendance, 
  getLastAttendanceActivity,
} from "../../../api/s10OrganizationApi";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import MySnackbar from "../../../components/MySnackbar";
import Webcam from "react-webcam";
import { getDatestring } from "../../../utils/date";
import { v4 as uuidv4 } from "uuid";
import { uploadFilesToFilestorage } from "../../../utils/media-files";

interface Attendance {
  EmployeeId: number;
  FullName: string;
  DocumentNumber: string;
  Date: string;
  Time: string;
  MarkType: string;
  HasEntranceOnThisDay: boolean;
  Longitude?: number;
  Latitude?: number;
  RemoteNeedLocation: boolean;
  RemoteNeedPhoto: boolean;    
}

interface Activity {
  Id: number;
  EmployeeId: number;
  DateTimeActivity: string;
  DailyActivities: string;
}

interface AttendanceConfig {
  requiresPhoto: boolean;
  requiresLocation: boolean;
}

interface ApiResponse<T> {
  Type: number;
  Message: string;
  Value: T | null;
  Validates: any;
}

export interface Location {
  latitude: number;
  longitude: number;
  accuracy?: number;
}

const AttendancePage: React.FC = () => {
  const { companyselected, connectionId, sessionData } = useSelector((state: RootState) => state.auth);

  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [lastAttendance, setLastAttendance] = useState<Attendance | null>(null);
  const [dailyActivity, setDailyActivity] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [successMsg, setSuccessMsg] = useState<string>("");
  const [employeeId, setEmployeeId] = useState<number | null | undefined>();
  const [loading, setLoading] = useState<boolean>(false);

  const [location, setLocation] = useState<Location | null>(null);
  const [locationError, setLocationError] = useState<string>("");
  const [isLocationLoading, setIsLocationLoading] = useState<boolean>(true);
  const [mapModalOpen, setMapModalOpen] = useState<boolean>(false);
  const [shouldRetry, setShouldRetry] = useState(false);
  const [userIP, setUserIP] = useState<string>("");
  
  const [attendanceConfig, setAttendanceConfig] = useState<AttendanceConfig>({
    requiresPhoto: false,
    requiresLocation: true
  });
  const [photoModalOpen, setPhotoModalOpen] = useState<boolean>(false);
  const [photoData, setPhotoData] = useState<string | null>(null);
  const [isTakingPhoto, setIsTakingPhoto] = useState<boolean>(false);
  const [pendingMarkType, setPendingMarkType] = useState<0 | 1 | null>(null);
  
  const webcamRef = useRef<Webcam>(null);
  const IS_LOCATION_REQUIRED = attendanceConfig.requiresLocation;

  const DEFAULT_ERROR_MESSAGE = "No estás autorizado para realizar marcaciones remotas.";

  const fetchUserIP = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      setUserIP(data.ip);
    } catch (error) {
      console.error("Error obteniendo IP:", error);
      setUserIP("No disponible");
    }
  };

  const requestLocationPermission = async (): Promise<boolean> => {
    if (!navigator.geolocation) {
      setLocationError("Tu navegador no soporta geolocalización, por favor intenta con otro navegador.");
      return false;
    }

    return new Promise((resolve) => {
      navigator.permissions?.query({ name: "geolocation" })
        .then((permissionStatus) => {
          if (permissionStatus.state === "granted") {
            resolve(true);
          } else if (permissionStatus.state === "prompt") {
            resolve(true);
          } else {
            setLocationError("Permiso de ubicación denegado. Por favor, habilita la ubicación en tu navegador.");
            resolve(false);
          }
        })
        .catch(() => {
          resolve(true);
        });
    });
  };

  const getCurrentLocation = () => {
    setIsLocationLoading(true);
    setLocationError('');
    setLocation(null);
    setShouldRetry(false);
  
    const options = {
      enableHighAccuracy: true,
      timeout: 30000,
      maximumAge: 0 
    };
  
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newLocation: Location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        };
        setLocation(newLocation);
        setIsLocationLoading(false);
      },
      (error) => {
        setIsLocationLoading(false);
        let errorMessage = '';
        let allowRetry = true;
  
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Permiso de ubicación denegado. ';
            errorMessage += 'Por favor, habilita la ubicación en la configuración de tu navegador o dispositivo y haz clic en "Reintentar".';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'La información de ubicación no está disponible. Reintentar podría ayudar.';
            break;
          case error.TIMEOUT:
            errorMessage = `La solicitud de ubicación tardó demasiado (${options.timeout/1000} segundos). `;
            errorMessage += 'Asegúrate de tener una buena señal (GPS/Wi-Fi) y haz clic en "Reintentar".';
            break;
          default:
            errorMessage = 'Ocurrió un error desconocido al obtener la ubicación.';
            break;
        }
        setLocationError(errorMessage);
        setShouldRetry(allowRetry);
      },
      options
    );
  };

  const dataURLtoFile = (dataurl: string, filename: string): File => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    
    return new File([u8arr], filename, { type: mime });
  };

  const uploadPhotoToS3 = async (photoBase64: string): Promise<string | null> => {
    if (!sessionData?.UserId) {
      setErrorMsg("No se pudo identificar al empleado.");
      return null;
    }

    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const fileId = `${uuidv4()}`;
    const fileName = `attendances/${year}/${month}/${day}/${sessionData?.UserId}-${fileId}`;
    const file = dataURLtoFile(photoBase64, `${fileId}.jpg`);
    const result = await uploadFilesToFilestorage(
      [file], 
      setErrorMsg,
      false,
      uuidv4(),
      false,
      [fileName]
    );

    if (result && result.fileS3Urls && result.fileS3Urls.length > 0) {
      return result.fileS3Urls[0];
    }
    
    return null;
  };

  useEffect(() => {
    const initLocation = async () => {
      if (IS_LOCATION_REQUIRED) {
        const hasPermission = await requestLocationPermission();
        if (hasPermission) {
          getCurrentLocation();
        } else {
          setIsLocationLoading(false);
        }
      } else {
        setIsLocationLoading(false);
      }
    };

    fetchUserIP();
    initLocation();
  }, []);

  const areButtonsDisabled = () => {
    if (loading || !employeeId) return true;
    if (IS_LOCATION_REQUIRED && (!location || locationError)) return true;
    return false;
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchLastAttendance = async () => {
    const response: { data: ApiResponse<Attendance> } = await getLastAttendance();
    if (response.data.Type === 0 && response.data.Value) {
      setLastAttendance(response.data.Value);
      setEmployeeId(response.data.Value.EmployeeId);
      setAttendanceConfig({
        requiresLocation: response.data.Value?.RemoteNeedLocation || false,
        requiresPhoto: response.data.Value?.RemoteNeedPhoto || false,
      });
    } else {
      setErrorMsg(response.data.Message || DEFAULT_ERROR_MESSAGE);
      setEmployeeId(null);
    }
  };

  const fetchDailyActivity = async () => {
    const response: { data: ApiResponse<Activity> } = await getLastAttendanceActivity();
    if (response.data.Type === 0 && response.data.Value) {
      setDailyActivity(response.data.Value.DailyActivities || "");
    } else {
      setErrorMsg(response.data.Message || "Error obteniendo actividades diarias.");
    }
  };

  const handleActivitySubmit = async () => {
    if (!employeeId) {
      setErrorMsg(DEFAULT_ERROR_MESSAGE);
      return;
    }
    if (!dailyActivity) {
      setErrorMsg("El campo de actividades diarias debe ser completado.");
      return;
    }
    const response = await addAttendanceActivityMark(employeeId, dailyActivity);
    if (response.data.Type === 0) {
      setDailyActivity("");
      setSuccessMsg("Actividad registrada con éxito.");
    } else {
      setErrorMsg(response.data.Message || "Ocurrió un error registrando la actividad.");
    }
  };

  const capturePhoto = () => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      setPhotoData(imageSrc);
      setIsTakingPhoto(false);
    }
  };

  const handlePhotoConfirm = async () => {
    if (!photoData) {
      setErrorMsg("No se pudo capturar la foto. Por favor, inténtalo de nuevo.");
      return;
    }
    setPhotoModalOpen(false);
    if (pendingMarkType !== null && employeeId) {
      await processAttendanceMark(pendingMarkType, photoData);
    }
  };

  const processAttendanceMark = async (markType: 0 | 1, photoBase64?: string) => {
    if (!employeeId) {
      setErrorMsg(DEFAULT_ERROR_MESSAGE);
      return;
    }

    if (IS_LOCATION_REQUIRED) {
      if (!location || locationError) {
        setErrorMsg("No se puede realizar la marcación sin ubicación válida.");
        return;
      }
    }

    setLoading(true);
    
    let photoS3Url: string | null = null;
    if (photoBase64 && attendanceConfig.requiresPhoto) {
      photoS3Url = await uploadPhotoToS3(photoBase64);
      if (!photoS3Url) {
        setLoading(false);
        setErrorMsg("Error al subir la foto a la nube.");
        return;
      }
    }

    const response = await addRemoteAttendance(employeeId, markType, location, userIP, photoS3Url);
    setLoading(false);
    
    if (response.data.Type === 0) {
      setSuccessMsg(`Marcación de ${markType === 0 ? "entrada" : "salida"} registrada con éxito.`);
      setPhotoData(null);
      fetchLastAttendance();
      fetchDailyActivity();
    } else {
      setErrorMsg(response.data.Message || "Error en la marcación.");
    }
  };

  const handleAttendanceMark = async (markType: 0 | 1) => {
    if (attendanceConfig.requiresPhoto) {
      setPendingMarkType(markType);
      setPhotoModalOpen(true);
      setIsTakingPhoto(true);
    } else {
      await processAttendanceMark(markType);
    }
  };

  useEffect(() => {
    if (!companyselected || !connectionId || employeeId) {
      return;
    }
    fetchLastAttendance();
  }, [companyselected, connectionId]);

  useEffect(() => {
    if (!companyselected || !connectionId || !employeeId) {
      return;
    }
    fetchDailyActivity();
  }, [companyselected, connectionId, employeeId]);

  const openGoogleMaps = () => {
    if (location) {
      window.open(
        `https://www.google.com/maps?q=${location.latitude},${location.longitude}&z=17`,
        "_blank"
      );
    }
  };

  return (
    <Grid container>
      <AppBar position="static">
        <Toolbar>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography variant="h6" color="white">
              Fecha y Hora Actual: {getDatestring(currentTime, '-', false)} {currentTime.toLocaleTimeString()}
            </Typography>
            <Grid container direction="column" alignItems="flex-end">
              {IS_LOCATION_REQUIRED && location && !locationError && (
                <Typography variant="body2" color="white">
                  ✓ Ubicación detectada
                </Typography>
              )}
              <Typography variant="caption" color="white">
                IP: {userIP || "Obteniendo..."}
              </Typography>
              {attendanceConfig.requiresPhoto && (
                <Typography variant="caption" color="white">
                  📸 Foto requerida para marcación
                </Typography>
              )}
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>

      <Grid container spacing={3} paddingTop={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ padding: 2, height: "100%" }}>
            <Typography variant="h6">Última Marcación</Typography>
            {lastAttendance
              ? (
                <>
                  <Typography>Fecha: {lastAttendance.Date}</Typography>
                  <Typography>Hora: {lastAttendance.Time}</Typography>
                  <Typography>Tipo: {lastAttendance.MarkType}</Typography>
                </>
              )
              : employeeId === null
                ? (
                  <Typography>Cargando...</Typography>
                )
                : <></>
            }
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ padding: 2 }}>
            <Typography variant="h6">Actividades Diarias</Typography>
            <TextField
              fullWidth
              multiline
              disabled={!employeeId}
              rows={4}
              value={dailyActivity}
              onChange={(e) => setDailyActivity(e.target.value)}
              placeholder="Describe tus actividades diarias..."
            />
            <Button
              disabled={!employeeId}
              variant="contained"
              color="primary"
              onClick={handleActivitySubmit}
              sx={{ marginTop: 2 }}
            >
              Guardar Actividades
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={3} sx={{ padding: 2, textAlign: "center" }}>
            <Button
              variant="contained"
              color="success"
              disabled={areButtonsDisabled()}
              onClick={() => handleAttendanceMark(0)}
              sx={{ marginRight: 2 }}
            >
              Marcar Entrada
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={areButtonsDisabled()}
              onClick={() => handleAttendanceMark(1)}
            >
              Marcar Salida
            </Button>

            {IS_LOCATION_REQUIRED && isLocationLoading && (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mt: 2 }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <Typography variant="body2">Obteniendo ubicación...</Typography>
              </Box>
            )}

            {IS_LOCATION_REQUIRED && locationError && !isLocationLoading && (
              <Box sx={{ mt: 0 }}>
                <Typography color="error" variant="body2">
                  {locationError}
                </Typography>
                {shouldRetry && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={requestLocationPermission}
                    sx={{ mt: 1 }}
                  >
                    Reintentar Obtención de Ubicación
                  </Button>
                )}
                {locationError.includes('denegado') && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Para habilitar los permisos manualmente, ve a la configuración de tu navegador, busca "Configuración del sitio" o "Permisos de ubicación" y actualiza los permisos para este sitio.
                  </Typography>
                )}
              </Box>
            )}

            {IS_LOCATION_REQUIRED && location && !locationError && !isLocationLoading && (
              <Box sx={{ mt: 2 }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => setMapModalOpen(true)}
                  sx={{ cursor: "pointer" }}
                >
                  Ver ubicación detectada
                </Link>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={photoModalOpen}
        onClose={() => {
          setPhotoModalOpen(false);
          setPendingMarkType(null);
          setPhotoData(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Tomar foto para la marcación
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {isTakingPhoto && !photoData ? (
              <Box sx={{ width: '100%', maxWidth: 640 }}>
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  videoConstraints={{
                    width: 640,
                    height: 480,
                    facingMode: "user"
                  }}
                  style={{ width: '100%', borderRadius: 8 }}
                />
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={capturePhoto}
                    sx={{ mr: 2 }}
                  >
                    Capturar Foto
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setPhotoModalOpen(false);
                      setPendingMarkType(null);
                    }}
                  >
                    Cancelar
                  </Button>
                </Box>
              </Box>
            ) : photoData ? (
              <Box sx={{ textAlign: 'center' }}>
                <img
                  src={photoData}
                  alt="Captura de asistencia"
                  style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 8 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Foto capturada. ¿Deseas usar esta foto para la marcación?
                </Typography>
              </Box>
            ) : null}
          </Box>
        </DialogContent>
        {photoData && (
          <DialogActions>
            <Button
              onClick={() => {
                setPhotoData(null);
                setIsTakingPhoto(true);
              }}
            >
              Tomar otra foto
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePhotoConfirm}
            >
              Usar esta foto
            </Button>
          </DialogActions>
        )}
      </Dialog>

      <Modal
        open={mapModalOpen}
        onClose={() => setMapModalOpen(false)}
        aria-labelledby="map-modal-title"
        aria-describedby="map-modal-description"
      >
        <Box sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: 800,
          height: "80%",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 0,
          borderRadius: 1,
          overflow: "hidden"
        }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
            <Typography id="map-modal-title" variant="h6">
              Ubicación Detectada
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Latitud: {location?.latitude?.toFixed(6)}, Longitud: {location?.longitude?.toFixed(6)} {location?.accuracy && ` (Precisión: ${Math.round(location?.accuracy)}m)`}
            </Typography>
          </Box>
          <Box sx={{ width: '100%', minHeight: 'calc(100% - 150px)', height: 'calc(100% - 150px)' }}>
            {location && (
              <iframe
                title="OpenStreetMap"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${location.longitude - 0.01},${location.latitude - 0.01},${location.longitude + 0.01},${location.latitude + 0.01}&layer=mapnik&marker=${location.latitude},${location.longitude}`}
                allowFullScreen
              />
            )}
          </Box>
          <Box sx={{ p: 2, textAlign: "center", borderTop: 1, borderColor: "divider" }}>
            <Button
              variant="outlined"
              onClick={openGoogleMaps}
              sx={{ mr: 2 }}
            >
              Abrir en Google Maps
            </Button>
            <Button
              variant="contained"
              onClick={() => setMapModalOpen(false)}
            >
              Cerrar
            </Button>
          </Box>
        </Box>
      </Modal>

      <MySnackbar
        severity="error"
        msg={errorMsg}
        onClose={() => setErrorMsg("")}
        open={!!errorMsg}
      />

      <MySnackbar
        severity="success"
        msg={successMsg}
        onClose={() => {
          setSuccessMsg("");
        }}
        open={!!successMsg}
      />
    </Grid>
  );
};

export default AttendancePage;